import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ScorePill, StatusBadge } from "@/components/StatusBadge";
import { PLANO_LABELS, FASE_LABELS, CLIMA_LABELS, brl, fmtPct, monthLabel } from "@/lib/format";
import { ArrowLeft, Download, AlertOctagon, AlertCircle, Sparkles, ListChecks, CalendarDays } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const ESFERA_LABEL = { federal: "Federal", estadual: "Estadual", municipal: "Municipal" };

export default function ExecutiveReport() {
    const { cid } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get(`/report/executive/${cid}`).then(r => setData(r.data));
    }, [cid]);

    if (!data) return <div className="text-sm text-slate-500">Carregando relatório executivo…</div>;

    const c = data.contract;

    return (
        <div className="space-y-6 fade-in max-w-5xl" data-testid="executive-report">
            <div className="no-print flex items-center justify-between">
                <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-[#0F2044] inline-flex items-center gap-1" data-testid="exec-back">
                    <ArrowLeft className="h-3 w-3" /> Voltar
                </button>
                <button onClick={() => window.print()} data-testid="exec-print" className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold inline-flex items-center gap-2">
                    <Download className="h-4 w-4" /> Exportar PDF
                </button>
            </div>

            <header className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Relatório Executivo do Cliente</div>
                <div className="mt-1 flex items-start justify-between gap-6">
                    <div>
                        <h1 className="font-display font-bold text-3xl text-[#0F2044]">{c.organizacao_nome}</h1>
                        <div className="text-sm text-slate-500 mt-1">Esfera {ESFERA_LABEL[c.esfera] || c.esfera || "—"}</div>
                        <div className="text-sm text-slate-600 mt-2">
                            {PLANO_LABELS[c.plano]} · Fase {FASE_LABELS[data.summary.fase_atual]} · Vendedor {c.vendedor_responsavel || c.cs_responsavel || "—"}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Assinatura {brl(c.data_assinatura)} · Vencimento {brl(c.data_vencimento)}{c.data_entrega_produto ? ` · Entrega ${brl(c.data_entrega_produto)}` : ""}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Score atual</div>
                        <div className="mt-2"><ScorePill score={data.summary.score} /></div>
                    </div>
                </div>
            </header>

            <Section title="Resumo executivo" testid="exec-resumo">
                <div className="grid grid-cols-4 gap-4">
                    <Kpi label="Status" value={<StatusBadge status={data.summary.status} />} />
                    <Kpi label="Score" value={data.summary.score} />
                    <Kpi label="% Uso recente" value={fmtPct(data.summary.percentual_uso_recente)} />
                    <Kpi label="Clima recente" value={data.summary.clima_recente || "—"} />
                </div>
            </Section>

            {(data.riscos.length + data.atencoes.length + data.oportunidades.length) > 0 && (
                <Section title="Riscos · Atenção · Oportunidades" testid="exec-riscos">
                    <div className="grid grid-cols-3 gap-4">
                        <ListCard title="Riscos críticos" items={data.riscos} color="#EF4444" icon={AlertOctagon} testid="exec-risk-list" />
                        <ListCard title="Atenção" items={data.atencoes} color="#F59E0B" icon={AlertCircle} testid="exec-aten-list" />
                        <ListCard title="Oportunidades" items={data.oportunidades} color="#10B981" icon={Sparkles} testid="exec-opp-list" />
                    </div>
                </Section>
            )}

            <Section title="Histórico de interações recentes" testid="exec-historico">
                {data.historico_recente.length === 0 ? <Empty text="Sem registros." /> : (
                    <ul className="space-y-2">
                        {data.historico_recente.map((e, i) => (
                            <li key={i} className="text-sm border-l-2 border-slate-200 pl-3 py-1">
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <CalendarDays className="h-3 w-3" /> {brl(e.data)} · <span className="font-semibold uppercase tracking-wider">{e.tipo}</span>
                                </div>
                                <div className="font-medium text-[#0F2044]">{e.titulo}</div>
                                {e.descricao && <div className="text-slate-600 text-xs mt-0.5">{e.descricao}</div>}
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            <Section title="Uso da plataforma — últimos meses" testid="exec-uso">
                {data.usage_recent.length === 0 ? <Empty text="Sem dados de uso." /> : (
                    <>
                        {/* M13 — gráfico horizontal de uso por módulo */}
                        <div style={{ height: Math.max(160, data.usage_recent.length * 56) }} className="mb-5" data-testid="exec-uso-chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    layout="vertical"
                                    data={[...data.usage_recent].sort((a, b) => a.mes_ref.localeCompare(b.mes_ref)).map(u => ({
                                        mes: monthLabel(u.mes_ref),
                                        Mensagens: u.mensagens,
                                        Documentos: u.documentos,
                                        Analisador: u.analises,
                                    }))}
                                    margin={{ left: 8, right: 16 }}
                                >
                                    <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11 }} />
                                    <YAxis type="category" dataKey="mes" tick={{ fontSize: 11 }} width={70} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="Mensagens" fill="#0F2044" barSize={10} radius={[0, 3, 3, 0]} />
                                    <Bar dataKey="Documentos" fill="#3B82F6" barSize={10} radius={[0, 3, 3, 0]} />
                                    <Bar dataKey="Analisador" fill="#8B5CF6" barSize={10} radius={[0, 3, 3, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 border-b border-slate-100">
                            <th className="py-2 pr-3 font-medium">Mês</th><th className="py-2 pr-3 text-right font-medium">Mensagens</th>
                            <th className="py-2 pr-3 text-right font-medium">Documentos</th><th className="py-2 pr-3 text-right font-medium">Analisador</th>
                        </tr></thead>
                        <tbody>{data.usage_recent.map(u => (
                            <tr key={u.id} className="border-b border-slate-50">
                                <td className="py-1.5 pr-3 font-medium text-[#0F2044]">{monthLabel(u.mes_ref)}</td>
                                <td className="py-1.5 pr-3 text-right font-mono-tab">{u.mensagens}</td>
                                <td className="py-1.5 pr-3 text-right font-mono-tab">{u.documentos}</td>
                                <td className="py-1.5 pr-3 text-right font-mono-tab">{u.analises}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                    </>
                )}
            </Section>

            <Section title="Tarefas pendentes (próximas 10)" testid="exec-tasks">
                {data.tarefas_pendentes.length === 0 ? <Empty text="Nenhuma pendente." /> : (
                    <ul className="space-y-1.5">
                        {data.tarefas_pendentes.map(t => (
                            <li key={t.id} className="text-sm flex items-center gap-2">
                                <ListChecks className="h-3.5 w-3.5 text-slate-400" />
                                <span className="text-slate-700">{t.descricao}</span>
                                <span className="text-xs text-slate-400 ml-auto">{t.responsavel}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            {data.proximas_acoes.length > 0 && (
                <Section title="Próxima ação prevista" testid="exec-next">
                    {data.proximas_acoes.map((a, i) => (
                        <div key={i} className="text-sm">
                            <div className="text-[#0F2044] font-medium">{a.descricao}</div>
                            <div className="text-xs text-slate-500">Prazo: {brl(a.data)} · {a.responsavel}</div>
                        </div>
                    ))}
                </Section>
            )}

            <Section title="Treinamentos realizados" testid="exec-treinos">
                {data.treinamentos.length === 0 ? <Empty text="Nenhum treinamento registrado." /> : (
                    <ul className="text-sm space-y-1">
                        {data.treinamentos.map(t => (
                            <li key={t.id} className="text-slate-700">
                                <span className="font-mono-tab text-xs text-slate-500">{brl(t.data)}</span> · {(t.modulos && t.modulos.length ? t.modulos.join(", ") : t.modulo)} · {t.quantidade_participantes} part. · {t.duracao_minutos} min
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            {/* M19 — resumo das reuniões realizadas */}
            <Section title="Reuniões realizadas" testid="exec-reunioes">
                {(!data.reunioes || data.reunioes.length === 0) ? <Empty text="Nenhuma reunião registrada." /> : (
                    <ul className="space-y-3">
                        {data.reunioes.map(m => (
                            <li key={m.id} className="border-l-2 border-slate-200 pl-3" data-testid={`exec-reuniao-${m.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono-tab text-xs text-slate-500">{brl(m.data)}</span>
                                    <span className="text-sm font-medium text-[#0F2044]">{m.tipo}</span>
                                    {m.clima && <span className="badge-status" style={{ background: "#EEF2FF", color: "#4338CA" }}>Clima: {CLIMA_LABELS[m.clima] || m.clima}</span>}
                                </div>
                                {m.participantes && <div className="text-xs text-slate-500 mt-0.5">Participantes: {m.participantes}</div>}
                                {m.notas && <div className="text-sm text-slate-700 mt-1">{m.notas}</div>}
                                {m.proximos_passos && <div className="text-xs text-slate-600 mt-1"><strong>Próximos passos:</strong> {m.proximos_passos}</div>}
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            <div className="no-print text-center">
                <Link to={`/contratos/${cid}`} className="text-sm text-[#0F2044] hover:underline">← Voltar para o contrato</Link>
            </div>
        </div>
    );
}

function Section({ title, children, testid }) {
    return (
        <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testid}>
            <h2 className="font-display font-semibold text-lg text-[#0F2044] mb-4">{title}</h2>
            {children}
        </section>
    );
}
function Kpi({ label, value }) {
    return (
        <div className="border border-slate-100 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
            <div className="mt-1 text-2xl font-display font-semibold text-[#0F2044]">{value}</div>
        </div>
    );
}
function ListCard({ title, items, color, icon: Icon, testid }) {
    return (
        <div className="border border-slate-100 rounded-md p-3" data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                <Icon className="h-4 w-4" style={{ color }} />
                <h3 className="text-sm font-semibold" style={{ color }}>{title}</h3>
            </div>
            {items.length === 0 ? <div className="text-xs text-slate-400">—</div> : (
                <ul className="space-y-1.5 text-xs text-slate-700">
                    {items.slice(0, 6).map((i, idx) => (<li key={idx}>{i.gatilho}</li>))}
                </ul>
            )}
        </div>
    );
}
function Empty({ text }) { return <div className="text-sm text-slate-500 py-2">{text}</div>; }
